'use client'
import { cn } from '@lib/utils'
/* eslint-disable spaced-comment */

import {
  type MouseEventHandler,
  useRef,
  type AriaRole,
  type MediaHTMLAttributes,
  useEffect,
  type ReactEventHandler,
} from 'react'

interface VideoSource {
  url: string
  contentType: string
}

export type InputVideoSource =
  | {
      [P in keyof VideoSource]+?: VideoSource[P] | null
    }
  | null
  | undefined

type VideoProps = MediaHTMLAttributes<HTMLVideoElement> & {
  videos: InputVideoSource[]
  role?: AriaRole | undefined
  className?: string | undefined
  screenReaderFallbackText?: string
  playOnHover?: boolean
  playOnIntersect?: boolean
  intersectionDelayStartMs?: number
  offsetStartSeconds?: number
  delayLoopMs?: number
  playbackRate?: number
  DEBUG_TIMESTAMP?: boolean
}

const intersectionObserverDefaultOptions: IntersectionObserverInit = {
  rootMargin: '0px',
  threshold: 0.9,
}

const deviceCanHover = () =>
  typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches

const isFilledVideoSource = (input: InputVideoSource): input is VideoSource =>
  input?.url != null && input.contentType != null

/**
 * NOTE that some props are conflicting.
 * eg. playOnHover vs playOnIntersect vs autoPlay.
 * The order of precedence is playOnIntersect -> playOnHover -> autoPlay
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export const Video = (props: VideoProps) => {
  const {
    videos: propsVideos,
    className,
    role,
    screenReaderFallbackText,
    muted,
    loop,
    delayLoopMs,
    playOnHover: propsPlayOnHover,
    playOnIntersect: propsPlayOnIntersect,
    intersectionDelayStartMs: propsIntersectionDelayStartMs = 0,
    playbackRate,
    offsetStartSeconds,
    playsInline,
    autoPlay: propsAutoPlay,
    DEBUG_TIMESTAMP = false,
  } = props

  if (propsPlayOnHover && propsPlayOnIntersect) {
    throw new Error(
      '<Video> cannot be used with both playOnHover & playOnIntersect',
    )
  }

  const videos: VideoSource[] = (
    typeof structuredClone === 'function'
      ? structuredClone(propsVideos)
      : [...propsVideos]
  ).filter((v) => isFilledVideoSource(v))

  /**
   * add delay via Media Fragments API
   * https://www.w3.org/TR/media-frags/#naming-time
   */
  if (offsetStartSeconds != null) {
    for (const v of videos) v.url += `#t=${offsetStartSeconds}`
  }

  const videoRef = useRef<HTMLVideoElement>(null)
  const hasStarted = useRef<boolean>(false)
  const isIntersecting = useRef<boolean>(false)
  const intersectionDelayStartMs = useRef<number>(propsIntersectionDelayStartMs)
  const isHovering = useRef<boolean>(false)
  const replayTimeout = useRef<number | null>(null)

  const canHover = deviceCanHover()
  /**
   * PlayOnIntersect should override
   */
  const playOnHover = canHover
    ? propsPlayOnHover === true
    : propsPlayOnIntersect === false
  const autoPlay = canHover
    ? !playOnHover && !propsPlayOnIntersect && propsAutoPlay
    : propsAutoPlay

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (videoRef.current === null) {
      return
    }

    if (!autoPlay) {
      videoRef.current.pause()
    }
    if (playbackRate != null) {
      videoRef.current.playbackRate = playbackRate
    }

    let intersectionObserver: IntersectionObserver | null = null
    let timeout: number | null = null
    let startIntersectingTime: number | null = null
    if (propsPlayOnIntersect) {
      videoRef.current.pause()
      intersectionObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            isIntersecting.current = true
            startIntersectingTime = Date.now()
            // If this video has already starting playing before, don't apply the delay
            if (hasStarted.current || intersectionDelayStartMs.current === 0) {
              videoRef.current?.play()
              hasStarted.current = true
            } else {
              timeout = window.setTimeout(() => {
                videoRef.current?.play()
                hasStarted.current = true
                intersectionDelayStartMs.current = 0
              }, intersectionDelayStartMs.current)
            }
          } else {
            // IntersectionObserver handler is called on page load with .isIntersecting: false,
            // so we need to handle that initial call without causing state issues.
            // Thus, we do some logic inside our timeout
            // - we will have only started the timeout if we are also doing intersectionDelayStartMs handling
            if (timeout !== null) {
              if (!hasStarted.current) {
                const deltaTime = Date.now() - (startIntersectingTime ?? 0)
                intersectionDelayStartMs.current =
                  intersectionDelayStartMs.current - deltaTime
              }

              window.clearTimeout(timeout)
              timeout = null
            }
            isIntersecting.current = false
            videoRef.current?.pause()
          }
        }
      }, intersectionObserverDefaultOptions)
      intersectionObserver.observe(videoRef.current)
    }

    return () => {
      if (intersectionObserver !== null) {
        intersectionObserver.disconnect()
        intersectionObserver = null
      }
      if (timeout !== null) {
        window.clearTimeout(timeout)
      }
    }
  }, [])

  const onEnded: ReactEventHandler<HTMLVideoElement> = () => {
    replayTimeout.current = window.setTimeout(
      () => {
        const noUxOverrides = !playOnHover && !propsPlayOnIntersect
        const hovering = playOnHover && (isHovering.current || !canHover)
        const intersecting = propsPlayOnIntersect && isIntersecting.current
        if (videoRef.current && (noUxOverrides || hovering || intersecting)) {
          videoRef.current.currentTime = 0
          videoRef.current.play()
        }
      },
      delayLoopMs == null ? 0 : delayLoopMs,
    )
    // also set the delay on intersection observer starting the video
    // intsersection observer has for intersectionDelayStartMs != 0
    // and will handle scrolling in/out of view again
    intersectionDelayStartMs.current = delayLoopMs == null ? 0 : delayLoopMs
  }

  const onMouseEnter: MouseEventHandler<HTMLVideoElement> = () => {
    isHovering.current = true
    if (videoRef.current) {
      if (replayTimeout.current != null) {
        window.clearTimeout(replayTimeout.current)
        replayTimeout.current = null
      }
      if (videoRef.current.ended) {
        videoRef.current.currentTime = 0
      }
      videoRef.current.play()
    }
  }
  const onMouseLeave: MouseEventHandler<HTMLVideoElement> = () => {
    isHovering.current = false
    if (videoRef.current) {
      videoRef.current.pause()
    }
  }

  const spanRef = useRef<HTMLSpanElement>(null)
  const onTimeUpdate: ReactEventHandler<HTMLVideoElement> = () => {
    if (spanRef.current) {
      spanRef.current.textContent =
        videoRef.current?.currentTime.toFixed(6) ?? null
    }
  }

  return (
    <div className='relative'>
      <video
        x-webkit-airplay='deny'
        ref={videoRef}
        autoPlay={autoPlay}
        muted={muted}
        playsInline={playsInline}
        className={cn(className, 'relative')}
        role={role}
        loop={loop && delayLoopMs == null}
        onEnded={loop && delayLoopMs != null ? onEnded : undefined}
        onMouseEnter={playOnHover && canHover ? onMouseEnter : undefined}
        onMouseLeave={playOnHover && canHover ? onMouseLeave : undefined}
        onTimeUpdate={DEBUG_TIMESTAMP ? onTimeUpdate : undefined}
      >
        {screenReaderFallbackText && (
          <span className='sr-only'>{screenReaderFallbackText}</span>
        )}
        {videos.map(
          (video) =>
            video != null && (
              <source
                key={video.url}
                src={video.url}
                type={video.contentType}
              />
            ),
        )}
      </video>
      {DEBUG_TIMESTAMP && (
        <span
          ref={spanRef}
          className='absolute left-0 right-0 top-0 z-10 bg-slate-800/75 p-1 px-3 text-left font-mono font-bold text-white'
        >
          0.000000
        </span>
      )}
    </div>
  )
}
